import React, { FC } from 'react';
import { Space, Typography, List, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Employee } from '~/types';
import { employees } from '~/routes';
import { EmployeeIconIndicators } from './EmployeesTable';
import { usePageShow } from '~/hooks/usePageQuery';
import { Card } from '~/components';
import theme from '~/theme';
import { useEmployeesRoles } from '~/hooks/employees';
import { getEmployeeRole } from '~/utils/getEmployeeRole';
import { getRoleColor } from '~/utils/employees';
import { useIsSelf } from '~/hooks/identity';

interface Props {
  data: Employee;
  loading: boolean;
  hasAccessView: boolean;
  className?: string;
}

const EmployeeCard: FC<Props> = (props) => {
  const { data, loading, className, hasAccessView } = props;
  const { firstName, lastName, roles, authority, email } = data || {};
  const { t } = useTranslation('employees');
  const dt = (key: string) => t(`translation:${key}`);
  const { showOpen } = usePageShow(employees);
  const { data: employeeRoles } = useEmployeesRoles();
  const isSelf = useIsSelf(email);
  return (
    <Card
      className={className}
      loading={loading}
      onClick={() => hasAccessView && showOpen(data.id as string)}
    >
      <List.Item.Meta
        title={
          <Typography.Title level={4} className="EmployeeCard-Title">
            <Space size={4}>
              <div className="EmployeeCard-Name">{`${firstName || ''} ${lastName || ''} ${
                isSelf ? `(${dt('you')})` : ''
              }`}</div>
              <EmployeeIconIndicators record={data} />
            </Space>
          </Typography.Title>
        }
        description={
          <Typography.Text ellipsis={true}>
            <Space direction="vertical">
              <Space size={4} className="EmployeeCard-Tags">
                {roles?.map((item: string) => (
                  <Tag key={item} color={getRoleColor(item)} className="EmployeeCard-Tag">
                    {getEmployeeRole(item, employeeRoles)}
                  </Tag>
                ))}
              </Space>
              <Typography.Text className="EmployeeCard-Position">
                {authority?.position}
              </Typography.Text>
            </Space>
          </Typography.Text>
        }
      />
    </Card>
  );
};

export default styled(EmployeeCard)`
  white-space: nowrap;
  height: 121px;
  cursor: pointer;
  .ant-space {
    display: flex;
    .ant-space-item {
      display: flex;
      max-width: 100%;
    }
  }
  .EmployeeCard-Title {
    .EmployeeCard-Name {
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .ant-space-item {
      max-width: 85%;
    }
  }
  .EmployeeCard-Position {
    overflow: hidden;
    text-overflow: ellipsis;
  }
  @media (max-width: ${theme.breakpoints.sm}px) {
    .ant-list-item-meta-avatar {
      display: none;
    }
  }
`;
